import { Button, CButtonClassname, EButtonColor, SectionTitle, useDoubleClickProtection } from '@web_opxp/components';
import { ConfigSelectors, ProgressSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CelebrationBadge, CelebrationLottie } from '../../achievements';
import { SKELETON_CLASSNAME } from '../../base/constants';
import { CyclesSelectors } from '../../cycles';
import { SessionSelectors } from '../../session';
import { IState } from '../../store/state';
import { challengeCompleteButtonClicked } from '../firebaseActions';
import * as Selectors from '../selectors';
import { ExerciseActions } from '../slice';
import './ChallengeCelebrationView.scss';

export interface IChallengeCelebrationViewProps {
  show: boolean;
  exerciseLink: string;
  exerciseId: string;
  score: number;
  nextWorkoutReady: boolean;
  isFirstSession: boolean;
  nextIndex: number;
  onFirstSessionComplete: () => void;
  onSessionComplete: () => void;
  onChallengeComplete: () => void;
}

export const ChallengeCelebrationViewFC: React.FC<IChallengeCelebrationViewProps> = ({
  show,
  exerciseLink,
  exerciseId,
  score,
  nextWorkoutReady,
  isFirstSession,
  nextIndex,
  onFirstSessionComplete,
  onSessionComplete,
  onChallengeComplete,
}) => {
  const { t } = useTranslation();
  const [showCelebration, setShowCelebration] = useState(false);
  const [showLottie, setShowLottie] = useState(false);
  const animationTimeout = useRef<any>();
  const clearAnimationTimeout = useCallback(() => {
    clearTimeout(animationTimeout.current);
    animationTimeout.current = undefined;
  }, []);

  // start waiting for lottie to end
  useEffect(() => {
    if (!showCelebration) {
      return;
    }
    if (animationTimeout.current) {
      clearAnimationTimeout();
    }
    setShowLottie(true);

    return clearAnimationTimeout;
  }, [clearAnimationTimeout, showCelebration]);
  // trigger showing the component when show flag is set
  useEffect(() => {
    setShowCelebration(show);
  }, [show]);

  const onChallengeClickHandler = useDoubleClickProtection(onChallengeComplete);

  if (!showCelebration) {
    return null;
  }
  const titleClasses = 'celebration-title';
  const lottieName = `Fireworks_Great`;
  const badgeAnimationClass = `badge-animation-container`;
  const nextButtonSkeleton = nextWorkoutReady ? '' : SKELETON_CLASSNAME;

  const handleCompleteClick = () => {
    if (isFirstSession) {
      onFirstSessionComplete();
      return;
    }

    onSessionComplete();
  };

  return (
    <div id="celebration-view" className="view">
      <SectionTitle className={titleClasses}>{t('ChallengeBadge.Title')}</SectionTitle>
      <div className="celebration-container">
        <div className={badgeAnimationClass}>
          <CelebrationBadge exerciseId={exerciseId} score={score} />
        </div>
      </div>
      <CelebrationLottie name={lottieName} show={showLottie} />
      <div className="celebration-action">
        {exerciseLink ? (
          <Link
            onClick={onChallengeClickHandler}
            className={`${CButtonClassname} ${EButtonColor.blue} ${nextButtonSkeleton} session-section-link`}
            to={{
              pathname: `${exerciseLink}`,
              state: { index: nextIndex },
            }}>
            {t('Exercise.PostWorkout.Actions.Continue')}
          </Link>
        ) : (
          <Button
            className={nextButtonSkeleton}
            label={t('Exercise.PostWorkout.Actions.Continue')}
            color={EButtonColor.blue}
            onClick={handleCompleteClick}
          />
        )}
      </div>
    </div>
  );
};

export const mapStateToProps = (state: IState) => {
  const loading = Selectors.isLoading(state);
  const show = !loading;
  const currentWorkoutId = Selectors.getWorkoutId(state);
  const score = ProgressSelectors.getLastWorkoutScore(state, currentWorkoutId);
  const exerciseName = ConfigSelectors.getWorkoutExerciseId(state, currentWorkoutId, true);
  const workoutId = SessionSelectors.getActiveWorkoutId(state);
  const exerciseId = ConfigSelectors.getWorkoutExerciseId(state, workoutId);
  const exerciseLink = exerciseId.toLowerCase();
  const sessionLoading = SessionSelectors.isLoading(state);
  const statsSaving = StatsSelectors.isSaving(state);
  const nextWorkoutReady = !sessionLoading && !statsSaving && !loading;
  const cycleId = CyclesSelectors.getCurrentCycleId(state);
  const sessionId = CyclesSelectors.getCurrentSessionId(state);
  const isFirstSession = cycleId === 1 && sessionId === 1;
  const nextIndex = SessionSelectors.getActiveWorkoutRelativeIndex(state);
  return {
    exerciseId: exerciseName,
    score,
    show,
    exerciseLink,
    nextWorkoutReady,
    isFirstSession,
    nextIndex,
  };
};

export const mapDispatchToProps = {
  onFirstSessionComplete: ExerciseActions.completeFirstSession,
  onSessionComplete: ExerciseActions.completeSession,
  onChallengeComplete: challengeCompleteButtonClicked,
};

const ChallengeCelebrationView = connect(mapStateToProps, mapDispatchToProps)(ChallengeCelebrationViewFC);
ChallengeCelebrationView.displayName = 'ChallengeCelebrationView';

export default ChallengeCelebrationView;
